// ─── src/utils/socialUrl.js ────────────────────────────────────────────────
// THE social-profile URL normaliser. One function, imported everywhere a stored
// social-field value (instagram, facebook, linkedin, tiktok, twitter, pinterest,
// youtube) is about to be rendered as a clickable href.
//
// Root Cause B (404 investigation, Class 2): social fields are stored as
// whatever was typed or scraped — sometimes a full URL, sometimes a bare
// handle (`astonbanduk`), sometimes an `@handle`. Rendered directly as an href,
// a bare value resolves as a RELATIVE link against the current page, producing
// a 404 at {current-listing-path}/{handle} instead of an external link.
//
// This is the single choke point: whether a value originated from a human
// typing it in Listing Studio or the AI import merging an extracted result,
// both converge into the same stored field before it's ever rendered. Fixing
// normalisation here, not at every write path, is sufficient — the same
// "fix the one canonical point" lesson as listingUrl.js (Project 1).
const PLATFORM_BASE = {
  instagram: 'https://instagram.com/',
  facebook:  'https://www.facebook.com/',
  tiktok:    'https://www.tiktok.com/@',
  twitter:   'https://twitter.com/',
  pinterest: 'https://www.pinterest.com/',
  youtube:   'https://www.youtube.com/@', // current canonical handle format, not a channel ID
  linkedin:  'https://www.linkedin.com/company/', // every LWD listing is a business, not a person
};

// Platforms whose real URLs never include an '@' in the path — a stored leading
// '@' on these is always a stray copy-paste artifact, not part of the URL.
const STRIP_AT = new Set(['instagram', 'tiktok', 'twitter', 'youtube']);

/**
 * Normalise one stored social-field value into a full, external URL.
 * Returns null for an empty/missing value, so callers can hide the link
 * instead of rendering a broken one — same contract as listingProfileUrl().
 *
 * @param {string} platform - one of PLATFORM_BASE's keys
 * @param {string|null|undefined} value - the raw stored value
 * @returns {string|null}
 */
export function socialProfileUrl(platform, value) {
  if (!value) return null;
  const v = String(value).trim();
  if (!v) return null;

  // Already a full URL — use as-is, whatever platform it claims to be for.
  if (/^https?:\/\//i.test(v)) return v;

  const base = PLATFORM_BASE[platform];
  if (!base) return null; // unroutable platform, refuse rather than emit a broken guess

  const handle = STRIP_AT.has(platform) ? v.replace(/^@/, '') : v;
  return `${base}${handle}`;
}
